import React from "react";
import { I18n } from "react-redux-i18n";
import cx from "classnames";

import { Loading } from "@common/components/loading";
import { getTestAttributes } from "@common/utils/tests";

import { FieldsProps } from "./fields.types";
import { Field } from "./components/field";
import { InternalFieldPrimaryList } from "../../spreadsheet-management.types";

import "./fields.styles.scss";

export const Fields: React.FC<FieldsProps> = (props) => {
  const { fields, isFieldsFromSearch, loading, className, selection, itemUniqProperty, onDrag, id, getId, onDoubleClick } = props;
  const anchorIdRef = React.useRef<InternalFieldPrimaryList[keyof InternalFieldPrimaryList] | null>(null);

  const getItemId = React.useCallback(
    (item: InternalFieldPrimaryList) => item[itemUniqProperty],
    [itemUniqProperty]
  );

  const mergeUniqueById = React.useCallback(
    (baseItems: InternalFieldPrimaryList[], extraItems: InternalFieldPrimaryList[]) => {
      const merged = [...baseItems];
      const seen = new Set(baseItems.map((x) => getItemId(x)));
      extraItems.forEach((item) => {
        const id = getItemId(item);
        if (!seen.has(id)) {
          seen.add(id);
          merged.push(item);
        }
      });
      return merged;
    },
    [getItemId]
  );

  const handleRowClickUp = React.useCallback(
    ({ item, event }: { item: InternalFieldPrimaryList; event: React.MouseEvent<HTMLDivElement> }) => {
      const alreadySelectedItems = selection?.items || ([] as InternalFieldPrimaryList[]);
      let newSelectedItems = [item];

      if (!(event.ctrlKey || event.shiftKey) && alreadySelectedItems.length > 1) {
        if (selection?.onChange) {
          selection.onChange(newSelectedItems);
        }
      }
    },
    [selection]
  );

  const handleRowClickDown = React.useCallback(
    ({ item, event }: { item: InternalFieldPrimaryList; event: React.MouseEvent<HTMLDivElement> }) => {
      const alreadySelectedItems = selection?.items || ([] as InternalFieldPrimaryList[]);
      let newSelectedItems = [item];
      const itemId = getItemId(item);
      const isAlreadySelected = alreadySelectedItems.some((x) => getItemId(x) === itemId);

      if (!event.shiftKey) {
        anchorIdRef.current = itemId;
      }

      if (!(event.ctrlKey || event.shiftKey) && alreadySelectedItems.length > 1) {
        return;
      } else if (event.shiftKey && selection?.type === "multiple") {
        const anchorId = anchorIdRef.current ?? itemId;
        const anchorIndex = fields.findIndex((field) => getItemId(field) === anchorId);
        const targetIndex = fields.findIndex((field) => getItemId(field) === itemId);

        if (anchorIndex !== -1 && targetIndex !== -1) {
          const startIndex = Math.min(anchorIndex, targetIndex);
          const endIndex = Math.max(anchorIndex, targetIndex);
          const rangeItems = fields.slice(startIndex, endIndex + 1);
          newSelectedItems = mergeUniqueById(alreadySelectedItems, rangeItems);
        } else {
          newSelectedItems = mergeUniqueById(alreadySelectedItems, [item]);
        }
      } else if (event.ctrlKey && selection?.type === "multiple") {
        newSelectedItems = isAlreadySelected
          ? alreadySelectedItems.filter((x) => getItemId(x) !== itemId)
          : alreadySelectedItems.concat(item);
      } else if (alreadySelectedItems.length === 1 && isAlreadySelected) {
        return;
      }

      if (selection?.onChange) {
        selection.onChange(newSelectedItems);
      }
    },
    [selection, getItemId, fields, mergeUniqueById]
  );

  const handleRowDoubleClick = React.useCallback(
    ({ item, event }: { item: InternalFieldPrimaryList; event: React.MouseEvent<HTMLDivElement> }) => {
      if (onDoubleClick) {
        onDoubleClick({ item, event });
      }
    },
    [onDoubleClick]
  );

  const isRowSelected = React.useCallback(
    (item: InternalFieldPrimaryList) => {
      return selection?.items.some((x) => x[itemUniqProperty] === item[itemUniqProperty]) ?? false;
    },
    [selection?.items, itemUniqProperty]
  );

  return (
    <div className={cx("SpreadsheetDetailsSpreadsheetManagementFields", className)} {...getTestAttributes({ id })}>
      {loading && <Loading size={20} margin="20px" />}
      {!loading && !fields.length && (
        <div className="SpreadsheetDetailsSpreadsheetManagementFields__no-data-message">{I18n.t("No data")}</div>
      )}
      {!loading && fields.length > 0 && (
        <div className="SpreadsheetDetailsSpreadsheetManagementFields__list">
          {fields.map((field, rowIndex) => (
            <Field
              key={field[itemUniqProperty]?.toString()}
              field={field}
              isFieldsFromSearch={isFieldsFromSearch}
              onMouseUp={handleRowClickUp}
              onMouseDown={handleRowClickDown}
              onDoubleClick={handleRowDoubleClick}
              isSelected={isRowSelected(field)}
              onDrag={onDrag}
              getId={{
                rowId: getId.rowId({ rowIndex }),
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
